import React, { useState, useEffect } from "react";
import PageDescription from "../../DataAnalyst/sections/PageDescription";
import FileUpload from "./FileUpload";
import MixesLibrary from "./MixesLibrary/MixesLibrary";
import MultiTrack from "./MultiTrack";
import Settings from "../AudioEngineer/sections/Settings";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChatInput from "../../AmplitudeAI/Chat/ChatInput";
import AppTour from "../../../../components/AppTour/AppTour";
import { useLocation } from "react-router-dom";
import PreviewSection from "./PreviewSection";
import { mixingEngineerTourSteps } from "../../../../components/AppTour/ModuleTourSteps";

export default function MixingEngineerDemo() {
    const location = useLocation();
    const [runTour, setRunTour] = useState(false);
    const [target, setTarget] = useState(null);
    const [showUpload, setShowUpload] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [showMultitrack, setShowMultitrack] = useState(false);
    const [newFile, setNewFile] = useState(true);
    const [genre, setGenre] = useState("ROCK_INDIE");
    const [sampleRate, setSampleRate] = useState("48000");

    useEffect(() => {
        if (location.hash === "#start-tour") {
            setRunTour(true);
        }
    }, [location.hash]);

    // Handle tour step changes
    const handleTourStepChange = (data) => {
        setTarget(data.step?.target);
    };

    const handleFileSelect = () => {
        setNewFile(false);
        setShowUpload(false);
        setShowSettings(true);
        // Simulate progression
        setTimeout(() => {
            setShowSettings(false);
            setShowMultitrack(true);
        }, 2000);
    };

    const handleNew = () => {
        setNewFile(true);
        setShowUpload(true);
        setShowSettings(false);
        setShowMultitrack(false);
    };

    const handleSend = () => {
        alert("Demo: This would send a message to Amplitude AI in the live version");
    };

    return (
        <div className="DataAnalyst-page py-5">
            <ToastContainer />
            <AppTour
                tourSteps={mixingEngineerTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
                callback={handleTourStepChange}
                setTarget={setTarget}
            />
            <div className="container max-w-full">
                <PageDescription
                    title="Your Mixing Engineer - Demo Mode"
                    description="Create studio-quality mixes in minutes — powered by intelligent, genre-aware AI. (Demo with sample data)"
                />

                <div className="mt-4 p-4 md:p-8 bg-theme_clr1 rounded-2xl">
                    {/* Upload Section */}
                    <div data-tour="upload-section" className="transition-all duration-300">
                        <FileUpload onFileSelect={handleFileSelect} />
                    </div>

                    {/* Settings Section */}
                    <div data-tour="settings-section" className="max-w-[500px] bg-white rounded-lg p-6 mb-4 mx-auto transition-all duration-300 mt-8">
                        <Settings
                            genre={genre}
                            setGenre={setGenre}
                            sampleRate={sampleRate}
                            setSampleRate={setSampleRate}
                            showLoudness={false}
                        />
                        <button
                            onClick={() => {
                                setShowSettings(false);
                                setShowMultitrack(true);
                            }}
                            className="mt-4 w-full max-w-md bg-theme_clr4 text-white py-3 rounded-xl hover:opacity-90 transition-opacity font-medium block mx-auto"
                        >
                            Save Settings & Start Mixing
                        </button>
                    </div>

                    {/* Multitrack Section */}
                    <div data-tour="multitrack-section" className="transition-all duration-300 mt-8">
                        <MultiTrack demoMode onUploadTracks={handleNew} />
                    </div>

                    <div className="w-full flex justify-center mt-8 px-2 md:px-0" data-tour="preview-section">
                        <div className="w-full md:w-[80%]">
                            <PreviewSection demoMode />
                        </div>
                    </div>

                    <div className="w-full flex justify-center mt-8 px-2 md:px-0" data-tour="mixed-section">
                        <div className="w-full md:w-[80%]">
                            <PreviewSection demoMasterMode />
                        </div>
                    </div>
                </div>

                {/* Chat and Library - Always visible after initial steps */}
                <div className="mt-4" data-tour="amplitude-ai-chat">
                    <ChatInput
                        title="Plan your next move with your mixed tracks."
                        inputPlaceholder="Let Amplitude AI design the perfect release strategy."
                        onSend={handleSend}
                        isDisabled={false}
                    />
                </div>

                <div className="mt-4 bg-theme_clr1 text-white rounded-xl p-4" data-tour="mixes-library">
                    <MixesLibrary addNew={handleNew} handleSettings={() => { }} />
                </div>

                {/* Demo Info Box */}
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 rounded p-4">
                    <p className="text-blue-900 font-semibold mb-2">ℹ️ Demo Mode</p>
                    <p className="text-blue-800 text-sm">
                        This is a demonstration page showing the Mixing Engineer interface. All data is simulated for demo purposes.
                    </p>
                </div>
            </div>
        </div>
    );
}
